<template>
  <div class="flex flex-col h-full w-full">
    <div class="flex flex-col h-full bg-white p-5 rounded-xl overflow-auto">
      <!-- Back link -->
      <button
        type="button"
        class="self-start inline-flex items-center gap-1 text-sm text-gray-600 hover:text-primary-900 cursor-pointer mb-3"
        @click="goBack"
      >
        <v-icon size="18">mdi-arrow-left</v-icon>
        {{ $t("reports.voice_agent_cdr.back") }}
      </button>

      <!-- Header -->
      <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-xs font-mono text-gray-500 tracking-wide">
            {{ $t("reports.voice_agent_cdr.head_call_id") }}: {{ callId }}
          </span>
          <h2 class="text-xl font-semibold text-gray-800">
            {{ $t("reports.voice_agent_cdr.details_title") }}
          </h2>
        </div>

        <!-- Recording -->
        <div class="flex items-center gap-2">
          <CustomAudioPlayer
            v-if="recordingUrl"
            :src="recordingUrl"
            :autoplay="false"
            :loop="false"
          />
          <template v-else>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 bg-primary-900 hover:bg-primary-500 text-white text-sm rounded px-3 py-1.5 cursor-pointer disabled:opacity-60"
              :disabled="recordingLoading"
              @click="loadRecording"
            >
              <v-progress-circular
                v-if="recordingLoading"
                class="!h-4 !w-4"
                width="2"
                indeterminate
                color="white"
              />
              <v-icon v-else size="small">mdi-headphones</v-icon>
              {{ $t("reports.voice_agent_cdr.load_recording") }}
            </button>
            <span v-if="recordingError" class="text-xs text-red-500">
              {{ $t("reports.voice_agent_cdr.recording_unavailable") }}
            </span>
          </template>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-1 justify-center items-center py-16">
        <v-progress-circular indeterminate color="primary" />
        <span class="ml-3 text-gray-500">{{ $t("reports.voice_agent_cdr.loading") }}</span>
      </div>

      <!-- Error / not found -->
      <div
        v-else-if="error || !details"
        class="flex flex-col flex-1 justify-center items-center py-16 text-center"
      >
        <v-icon :color="error ? 'error' : 'grey'" size="48">
          {{ error ? "mdi-alert-circle-outline" : "mdi-text-box-remove-outline" }}
        </v-icon>
        <p class="mt-3 text-gray-500">{{ error || $t("reports.voice_agent_cdr.not_found") }}</p>
      </div>

      <!-- Content -->
      <div v-else class="flex flex-col gap-5">
        <!-- Metadata -->
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div v-for="meta in detailMeta" :key="meta.label" class="min-w-0">
              <div class="text-[11px] text-gray-400 uppercase tracking-wide">{{ meta.label }}</div>
              <div class="text-gray-800 break-all" dir="ltr">{{ meta.value }}</div>
            </div>
          </div>

          <!-- Tags (shown when present) -->
          <div v-if="tags.length" class="mt-4 pt-4 border-t border-gray-100">
            <div class="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
              <v-icon size="14" color="primary">mdi-tag-multiple-outline</v-icon>
              {{ $t("reports.voice_agent_cdr.tags_title") }}
            </div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="(tag, idx) in tags"
                :key="idx"
                class="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-900 border border-primary-100"
              >
                {{ tag }}
              </span>
            </div>
          </div>
        </div>

        <!-- Summary (shown when present; populated in the future) -->
        <div v-if="details.summary" class="bg-stone-50 rounded-xl border border-stone-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-text-box-outline</v-icon>
            {{ $t("reports.voice_agent_cdr.summary_title") }}
          </h3>
          <p class="text-sm text-gray-800 whitespace-pre-wrap" :dir="containsArabic(details.summary) ? 'rtl' : 'ltr'">
            {{ details.summary }}
          </p>
        </div>

        <!-- Transcript -->
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <h3 class="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <v-icon size="18" color="primary">mdi-message-text-outline</v-icon>
            {{ $t("reports.voice_agent_cdr.transcript_title") }}
          </h3>
          <div v-if="transcriptLines.length === 0" class="text-center py-6 text-gray-400 text-sm">
            {{ $t("reports.voice_agent_cdr.no_transcript") }}
          </div>
          <div v-else class="space-y-2 text-sm leading-relaxed">
            <div
              v-for="(line, idx) in transcriptLines"
              :key="idx"
              class="rounded-md px-3 py-2 border whitespace-pre-wrap break-words"
              :class="line.role === 'assistant'
                ? 'border-blue-100 bg-blue-50/50'
                : 'border-green-100 bg-green-50/50'"
              :dir="containsArabic(line.text) ? 'rtl' : 'ltr'"
            >
              <span
                class="font-semibold"
                :class="line.role === 'assistant' ? 'text-blue-700' : 'text-green-700'"
              >
                {{ line.role === "assistant"
                  ? $t("reports.voice_agent_cdr.role_assistant")
                  : $t("reports.voice_agent_cdr.role_user") }}:
              </span>
              {{ line.text }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import CustomAudioPlayer from "@/components/CustomAudioPlayer.vue";

export default {
  name: "voice-cdr-details",
  components: { CustomAudioPlayer },
  data() {
    return {
      loading: false,
      error: null,
      details: null,
      recordingUrl: null,
      recordingLoading: false,
      recordingError: false,
    };
  },
  computed: {
    callId() {
      return this.$route.query.call_id || "";
    },
    detailMeta() {
      const d = this.details || {};
      const t = (k) => this.$t("reports.voice_agent_cdr." + k);
      return [
        { label: t("head_caller"), value: d.caller_number || "-" },
        { label: t("head_dialed"), value: d.dialed_number || "-" },
        { label: t("head_duration"), value: this.formatDuration(d.duration) },
        { label: t("head_started"), value: d.started_at || "-" },
        { label: t("head_ended"), value: d.ended_at || "-" },
      ];
    },
    tags() {
      const t = this.details?.tags;
      return Array.isArray(t) ? t.filter(Boolean) : [];
    },
    transcriptLines() {
      const t = this.details?.transcript;
      if (!Array.isArray(t)) return [];
      const deduped = this.dedupeConsecutive(t, (m) => `${m.role}|${m.content}`);
      return deduped
        .map((m) => ({
          role: String(m.role || "").toLowerCase() === "assistant" ? "assistant" : "user",
          text: m.content || m.text || m.message || "",
        }))
        .filter((m) => m.text);
    },
  },
  methods: {
    ...mapActions(["GetVoiceCDRDetails", "GetVoiceRecording", "SetMessageDialog"]),

    goBack() {
      this.$router.back();
    },
    dedupeConsecutive(arr, keyFn) {
      const out = [];
      let prevKey = null;
      for (const item of arr) {
        const key = keyFn(item);
        if (key !== prevKey) out.push(item);
        prevKey = key;
      }
      return out;
    },
    containsArabic(text) {
      return /[؀-ۿ]/.test(String(text || ""));
    },
    formatDuration(seconds) {
      const total = Math.floor(Number(seconds));
      if (!Number.isFinite(total) || total < 0) return "-";
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    },

    async fetchDetails() {
      if (!this.callId) {
        this.error = this.$t("reports.voice_agent_cdr.not_found");
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const resp = await this.GetVoiceCDRDetails({ call_id: this.callId });
        if (resp && resp.success) {
          const result = resp.result || {};
          this.details = result.data || result || null;
        } else {
          this.error = resp?.message || this.$t("reports.voice_agent_cdr.not_found");
        }
      } catch (err) {
        this.error = err.response?.data?.message || err.message || "Failed to load details";
      } finally {
        this.loading = false;
      }
    },

    loadRecording() {
      this.recordingLoading = true;
      this.recordingError = false;
      this.GetVoiceRecording({
        call_id: this.callId,
        recording_path: this.details?.recording_path || "",
      })
        .then((resp) => {
          if (resp.success) {
            this.recordingUrl = URL.createObjectURL(resp.recording);
          } else {
            this.recordingError = true;
          }
        })
        .catch(() => {
          this.recordingError = true;
        })
        .finally(() => {
          this.recordingLoading = false;
        });
    },
  },
  mounted() {
    this.fetchDetails();
  },
};
</script>

<style scoped></style>
